import { TpAssetInfo } from '../../../data/assets/interfaces';
import styles from '../StartPage.module.scss';
import { useEffect, useState } from 'react';
import { useApiContext } from '../../../api';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { Spinner } from '../../../components/primitives/Spinner';
import Dashboard from '../../visual-analytics/dashboard/Dashboard';
import { SectionsIndexWrapper, getSectionsIndexContext } from '../../../components/sections-index/SectionsIndex';
import { PlaceholderMessage } from '../../../components/primitives/Messages';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectCatalogAsset, setCatalogAsset } from '../../../data/assets/queriedAssetsSlice';

interface TpAssetDashboardProps {
  ontology: any;
  dashboardtemplate: any;
}

/**
 * A start page element that displays a dashboard for all assets in the organization.
 * @param props
 */
const AssetDashboard = (props: TpAssetDashboardProps) => {
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const cachedAsset = useAppSelector(selectCatalogAsset);

  const [assetInfo, setAssetInfo] = useState<TpAssetInfo | null>(cachedAsset);
  const [isFetching, setIsFetching] = useState(!cachedAsset);
  const [notFound, setNotFound] = useState(false);

  const showMissingContentPlaceholder =
    props.dashboardtemplate.onMissingContent && !props.dashboardtemplate.onMissingContent.showError;

  useEffect(() => {
    // If we have a cached asset, use it immediately
    if (cachedAsset) {
      setAssetInfo(cachedAsset);
      setIsFetching(false);
      setNotFound(false);
      return;
    }

    // Only fetch if not cached
    setIsFetching(true);

    api.asset
      .fetchCatalogAsset()
      .then((fetchedAsset) => {
        setAssetInfo(fetchedAsset);
        setNotFound(false);
        dispatch(setCatalogAsset({ asset: fetchedAsset }));
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          setNotFound(true);
          if (!showMissingContentPlaceholder) {
            showErrorMessage('Catalog dashboard asset not found. Please contact your administrator.');
          }
          return;
        }

        console.error(error);
        showErrorMessage('Error loading catalog dashboard. Please contact your administrator.');
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [cachedAsset, api.asset, showMissingContentPlaceholder, dispatch]);

  if (isFetching) {
    return <Spinner />;
  }

  if (notFound && showMissingContentPlaceholder) {
    return <PlaceholderMessage message={props.dashboardtemplate.onMissingContent.placeholder} />;
  }

  if (!assetInfo) {
    return null;
  }

  const assetId = assetInfo.id;

  const sectionIndexContext = getSectionsIndexContext(`_start_page_${assetId}`);

  return (
    <SectionsIndexWrapper sectionIndexContext={sectionIndexContext}>
      <div className={styles.wrapper} key={assetId}>
        <Dashboard
          dashboardtemplate={props.dashboardtemplate}
          assetInfo={assetInfo}
          sectionIndexContext={sectionIndexContext}
        />
      </div>
    </SectionsIndexWrapper>
  );
};

export default AssetDashboard;
